<template>
    <div class="outer-container">
        <div class="container">
            <div class="column lhs">
                <div class="col-content">
                    <Vue3Lottie :animationData="blobJSON" :height="300" :width="300" />
                </div>
            </div>
            <div class="column rhs">
                <div class="col-content rhs">
                    <p class="title"><span class="large-text">Welcome to</span><br>Distributor Portal</p>
                    <hr />
                    <div>
                        <input v-model="email" placeholder="Email" type="email" />
                        <input v-model="password" placeholder="Password" type="password" />
                        <button class="link" @click="signIn">Sign In</button>

                        <!-- Recaptcha container for SMS MFA -->
                        <div id="recaptcha-container"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Vue3Lottie } from 'vue3-lottie'
import blobJSON from '../../assets/tria_blob_anim.json'

// Please adjust the path to your firebase config file if it's different
import { auth } from '../../firebase';
import {
    signInWithEmailAndPassword,
    getMultiFactorResolver,
    PhoneAuthProvider,
    RecaptchaVerifier,
    PhoneMultiFactorGenerator,
    TotpMultiFactorGenerator
} from 'firebase/auth';

export default {
    name: 'DistributorLogin',
    components: {
        Vue3Lottie,
    },
    data() {
        return {
            blobJSON,
        }
    },
    setup() {
        const email = ref('');
        const password = ref('');
        const router = useRouter();
        async function signIn() {
            try {
                // Step 1: Firebase login
                await signInWithEmailAndPassword(auth, email.value, password.value);
                const user = auth.currentUser;

                // Step 2: Get ID token
                const token = await user.getIdToken();

                // Step 3: Call backend for authorization
                const response = await fetch(process.env.VUE_APP_VORTEX_PORTAL_WS + `/auth/distributor`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ email: email.value }),
                });
                if (!response.ok) throw new Error('Unauthorized distributor');
                const data = await response.json();
                const distributorLogin = data.DistributorLogin;

                if (distributorLogin?.DistributorGUID) {
                    // Step 4: Store in localStorage
                    localStorage.setItem('distributorGuid', distributorLogin.DistributorGUID);
                    localStorage.setItem('distributorName', distributorLogin.Company_Name);
                    localStorage.setItem('userName', `${distributorLogin.First_Name} ${distributorLogin.Last_Name}`);
                    localStorage.setItem('userRole', distributorLogin.User_Role);

                    // Step 5: Redirect
                    router.push('/distributor/dashboard');
                } else {
                    alert('Access denied: No distributor mapping found');
                }
            } catch (e) {
                if (e.code === 'auth/multi-factor-auth-required') {
                    const resolver = getMultiFactorResolver(auth, e);
                    const hint = resolver.hints[0];

                    if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
                        const recaptchaVerifier = new RecaptchaVerifier(
                            'recaptcha-container',
                            { size: 'normal' },
                            auth
                        );

                        const phoneInfoOptions = {
                            multiFactorHint: hint,
                            session: resolver.session,
                        };

                        const phoneAuthProvider = new PhoneAuthProvider(auth);
                        const verificationId = await phoneAuthProvider.verifyPhoneNumber(
                            phoneInfoOptions,
                            recaptchaVerifier
                        );

                        const code = window.prompt('Enter SMS code:');
                        const cred = PhoneAuthProvider.credential(verificationId, code);
                        const assertion = PhoneMultiFactorGenerator.assertion(cred);
                        await resolver.resolveSignIn(assertion);
                        alert('MFA complete!');
                    } else if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
                        const code = window.prompt('Enter TOTP code:');
                        const assertion = TotpMultiFactorGenerator.assertionForSignIn(
                            hint,
                            code
                        );
                        await resolver.resolveSignIn(assertion);
                        alert('MFA complete!');
                    }
                } else {
                    console.error('Login error:', e.message);
                    alert(e.message);
                }

            }

        }
        return { email, password, signIn, router };
    }
}
</script>

<style scoped>
h1 {
    text-align: center;
}

hr {
    margin-left: auto;
}

.outer-container {
    background-color: #38434e;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100vw;
}

.container {
    display: flex;
    max-width: 980px;
    width: 100%;
}

.column {
    text-align: center;
    /* Center content within columns */
    align-content: center;
    /* Vertically center content within columns */
    margin: 0 auto;
}

.column.lhs {
    width: 40%;
}

.column.rhs {
    width: 60%;
}

.col-content {
    display: inline-block;
    vertical-align: middle;
    padding: 40px;
}

.col-content.rhs {
    max-width: 450px;
    min-height: 300px;
    text-decoration: none;
    border-radius: 20px;
    background-color: #f4f4f4;
    box-shadow: 4px 3px 8px rgba(0, 0, 0, 0.1);
    padding: 40px 40px 20px 40px;
}

p.title {
    font-size: 26px;
    margin: 20px 0px 10px 0px;
    font-family: 'Montserrat';
    text-align: center;
    line-height: 1.2;
    font-weight: bold;
}

.large-text {
    font-size: 36px;
}

.small-text {
    font-size: 14px;
}

.link {
    flex: 1;
    display: inline-block;
    margin-top: 20px;
    margin-bottom: 40px;
    border-radius: 20px;
    background-color: black;
    color: white;
    box-shadow: 4px 3px 8px rgba(0, 0, 0, 0.1);
    font-size: 16px;
    font-weight: bold;
}

.link:hover {
    color: black;
    background-color: #FE602F;
    font-weight: normal;
}

input {
    display: block;
    width: 100%;
    min-width: 300px;
    border-radius: 2px;
    border: 1px solid #e5e5e5;
    padding: 5px;
    margin: 5px 0;
}

hr {
    background-color: #FE602F;
    border: none;
    margin-left: auto;
    margin-bottom: 20px;
    width: 60px;
    height: 3px;
}

a {
    color: black;
    text-decoration: none;
}

a:hover {
    color: #e45f2b;
}
</style>